import type React from 'react'
import type { JSX } from 'react'
import type { IconNames, IconSizes } from 'atoms/Icon'
import type { AvatarCursor, AvatarPadding } from './Avatar.h'

import { useMemo } from 'react'
import {
  TextAligning,
  TextBox,
  TextBoxColors,
  TextBoxTypes,
} from 'atoms/TextBox'
import { combineFirstLettersOfWordsToString } from 'utils/helpers'
import {
  AvatarBorderColors,
  AvatarBorders,
  AvatarColors,
  AvatarCorners,
  AvatarFonts,
  AvatarPaddings,
} from './Avatar.h'
import { AvatarStyled } from './Avatar.styled'
import { AvatarImage } from './Templates/AvatarImage'

export interface IAvatarProps {
  title?: string
  imageUrl?: string
  hasFallbackImage?: boolean
  icon?: IconNames
  iconSize?: IconSizes
  width?: number
  height?: number
  font?: AvatarFonts
  corners?: AvatarCorners
  paddings?: AvatarPadding
  borders?: AvatarBorders
  borderColor?: AvatarBorderColors
  backgroundDefault?: AvatarColors
  backgroundHovered?: AvatarColors
  backgroundPressed?: AvatarColors
  cursor?: AvatarCursor
  className?: string // https://github.com/callstack/linaria/blob/master/docs/BASICS.md#overriding-styles
  style?: React.CSSProperties
  hover?: boolean
  small?: boolean
}

export const Avatar = ({
  title = '',
  imageUrl = '',
  width = 40,
  height = 40,
  font = AvatarFonts.Body4,
  corners = AvatarCorners.CORNERS_ROUND,
  paddings = AvatarPaddings.SIZE_0,
  borders = AvatarBorders.BORDER_0,
  borderColor = AvatarBorderColors.ACCENT_TERTIARY,
  backgroundDefault = AvatarColors.BACKGROUND_DARK,
  backgroundHovered,
  backgroundPressed,
  cursor,
  className = '',
  style,
  hover = false,
  small = false,
}: IAvatarProps): JSX.Element => {
  const formattedTitle = useMemo(() => {
    if (!title) return ''

    if (small) {
      return title[0].toUpperCase()
    }

    const words = title.split(' ')

    if (words.length === 1) {
      return title.slice(0, 2).toUpperCase()
    }

    return combineFirstLettersOfWordsToString(title, 2).toUpperCase()
  }, [title, small])

  return (
    <AvatarStyled
      imageUrl={imageUrl}
      width={width}
      height={height}
      corners={corners}
      paddings={paddings}
      borders={borders}
      borderColor={borderColor}
      backgroundDefault={backgroundDefault}
      backgroundHovered={backgroundHovered}
      backgroundPressed={backgroundPressed}
      cursor={cursor}
      className={className}
      style={style}
    >
      {imageUrl ? (
        <AvatarImage imageUrl={imageUrl} hover={hover} />
      ) : (
        <TextBox
          title={formattedTitle}
          font={TextBoxTypes[font]}
          alignment={TextAligning.Center}
          color={TextBoxColors.TEXT_ICON_PRIMARY}
          maxLines={2}
        />
      )}
    </AvatarStyled>
  )
}
